/**
 * The one-line confirmation, at the bottom of the screen.
 *
 * MOUNTED ONCE, FOR THE WHOLE SESSION — never conditionally rendered. A `role="status"` region
 * that is mounted at the same moment its text appears is not reliably announced: assistive tech
 * has to be watching the region *before* the change happens. That is why this element exists from
 * first paint with an empty string in it and only ever fades.
 *
 * Which then creates the opposite problem, and the provider solves it: the text is CLEARED 350ms
 * after the fade starts (`toast/clear`, 2750ms), because a live region that keeps its message goes
 * on holding stale news for a screen reader long after it has visually gone.
 *
 * `.ft-toast` is where it sits vertically — `calc(104px + env(safe-area-inset-bottom))` on a phone,
 * so it clears both the bottom bar and the home indicator, and 34px above 1120px where there is no
 * bottom bar. Everything else is inline, exactly as the prototype had it (dc.html:549).
 */

import type { ReactElement } from 'react'

import { usePortal } from '../state/portal'

export function Toast(): ReactElement {
  const { state } = usePortal()
  const { message, visible } = state.toast

  return (
    <div
      className="ft-toast"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        zIndex: 50,
        transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(14px)',
        opacity: visible ? 1 : 0,
        pointerEvents: 'none',
        background: 'var(--color-neutral-900)',
        color: 'var(--color-neutral-100)',
        padding: '12px 18px',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 600,
        boxShadow: 'var(--shadow-lg)',
        transition: 'opacity .25s, transform .25s',
        /*
          The prototype used `white-space:nowrap`, which was safe because every message it could
          produce was short and hand-written. Against the real API a toast can carry a server
          sentence ("We could not reach the portal. Please check your connection.") and nowrap
          sends it straight off both edges of a phone, centred on a point it has long since passed.
          A max-width plus normal wrapping keeps the designed single-line pill for the short case
          and degrades to a rounded block for the long one, instead of degrading to invisible.
        */
        maxWidth: 'min(92vw, 520px)',
        whiteSpace: 'normal',
        textAlign: 'center',
        lineHeight: 1.45,
      }}
    >
      {message}
    </div>
  )
}
